import { useCallback, useEffect, useId, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { controlplaneLogo, oMark } from "../../brand.ts";
import { useAuth } from "../../auth/index.ts";
import { authFetch } from "../../lib/auth-client.ts";
import {
  getLoginFactor,
  loginWithPassword,
  loginWithPasskeyResult,
  verifyEmailCode,
  ssoOutcomeFromQuery,
  type SsoForEmail,
} from "../../lib/account-client.ts";
import { AUTH_MESSAGES, friendly, friendlyAuthError, type FriendlyAuthError } from "./authErrors.ts";
import { validateEmail } from "../../lib/validate.ts";
import "./login.css";
import { OtpInput } from "../../components/OtpInput.tsx";
import { ParticleField } from "./ParticleField.tsx";
import { FloatingQuotes } from "./FloatingQuotes.tsx";
import { formatStatValue, type PublicStats, type PublicStatsLoader } from "../../lib/public-stats.ts";


/**
 * The first-party login/register screen (controlplane-auth task 12.1; requirements A1, A5; design
 * §Reference alignment "Login screen", DESIGN-DECISION-1/3). A TWO-PANEL split layout echoing the
 * AgentDNA dashboard: a LEFT brand panel (the OATHERA logo + O mark + a short tagline on the
 * navy/blue gradient) and a RIGHT form panel (the credential form on a light surface). On a narrow
 * viewport the brand panel collapses and only the form shows.
 *
 * It keeps THIS control plane's SERVER-SIDE SESSION model (design §Deliberate divergences 2): no
 * `localStorage` JWT. Every mechanism POSTs to a same-origin `/auth/*` endpoint that sets the
 * `oa_session` cookie server-side; on success we refresh the {@link useAuth} session and navigate in.
 *
 * Three auth entry points, all ending at the same session:
 *   1. Password + MFA — `POST /auth/login {email,password,code?}` (reveals a TOTP step on mfa_required).
 *   2. Email OTP — `POST /auth/start {email}` then `POST /auth/verify {email,code}`. Register uses this.
 *   3. OIDC SSO — a top-level navigation to `GET /auth/login?idp=…`.
 *
 * The role toggle, login/register modes, inline friendly errors and all submit handlers are unchanged
 * from the single-card version — only the surrounding layout is the new split. Served WITHOUT a
 * session; after a successful sign-in it returns the operator to the attempted path or `/`.
 */

/** The credential mechanism the operator is using. */
type Mechanism = "password" | "otp";
/** Login vs register (register uses OTP email verification per design). */
type Mode = "login" | "register";
/** The role-toggle affordance (copy only; the server resolves the real role). */
type RoleHint = "user" | "admin";

/** The multi-step state the password/OTP flows move through. */
type Step =
  | { readonly kind: "credentials" }
  | { readonly kind: "mfa" }
  | { readonly kind: "otp_sent" };

/** The success shape of a management/auth envelope the login POSTs may return. */
interface AuthEnvelope {
  readonly status?: boolean;
  readonly message?: string;
  readonly data?: unknown;
}

/** An IdP offered for SSO. */
export interface IdpOption {
  readonly id: string;
  readonly label: string;
}

export interface LoginPageProps {
  readonly idps?: readonly IdpOption[];
  readonly onSsoNavigate?: (href: string) => void;
  /** Loads the brand panel's platform totals. Omitted: no request is made and the pills show dashes. */
  readonly loadStats?: PublicStatsLoader;
}

/**
 * A live-metrics pill on the login brand panel. The values are platform totals across every team
 * from the public `GET /public/stats` route (src/worker/public-stats.ts), loaded by the page's
 * `loadStats` prop; until they arrive, or when one is unavailable, the pill shows a dash.
 */
interface LiveMetric {
  readonly key: keyof PublicStats;
  readonly glyph: string;
  readonly label: string;
}

const LIVE_METRICS: readonly LiveMetric[] = [
  // O: active users with an active membership in at least one team.
  { key: "humans", glyph: "O", label: "Humans" },
  // A: registered (not deleted) agents.
  { key: "agents", glyph: "A", label: "Agents secured" },
  // T: agent actions recorded (Gateway decisions plus Identity MCP operations).
  { key: "tether", glyph: "T", label: "Tether actions" },
  // H: distinct humans who authorized an agent enrollment.
  { key: "hta", glyph: "H", label: "Hold to account" },
];

/** Admin accounts are all under this org domain; the admin form only takes the local part. */
const ADMIN_EMAIL_DOMAIN = "oathera.ai";

/**
 * Build an admin's full email from whatever is in the username field. The field already holds just
 * the local part, but we defensively strip anything from an "@" onward (and whitespace) so a pasted
 * "rohan@oathera.ai" can never produce "rohan@oathera.ai@oathera.ai".
 */
function adminEmail(local: string): string {
  const localPart = (local.split("@", 1)[0] ?? "").replace(/\s+/g, "");
  return `${localPart}@${ADMIN_EMAIL_DOMAIN}`;
}

export function LoginPage(props: LoginPageProps): ReactNode {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mechanism, setMechanism] = useState<Mechanism>("password");
  const [mode, setMode] = useState<Mode>("login");
  const [roleHint, setRoleHint] = useState<RoleHint>("user");
  const isAdmin = roleHint === "admin";
  const currentYear = new Date().getFullYear();
  const [step, setStep] = useState<Step>({ kind: "credentials" });
  // The admin password sign-in is a two-step wizard: enter the userid (stage "userid"), click Next,
  // then the credential pane (password, plus the OTP boxes on mfa_required) slides in (stage
  // "credential"). Only used for the admin tab; the user tab keeps the single-form flow.
  const [adminStage, setAdminStage] = useState<"userid" | "credential">("userid");
  // Admin fallback affordances. `passkeyFailed` is set when an auto passkey login does not complete;
  // `pwFailCount` counts CONSECUTIVE failed password / password+OTP submits. Either reaching its
  // trigger (passkey failed, or 3 password failures) reveals the "email me a code" + "reset" buttons.
  const [passkeyFailed, setPasskeyFailed] = useState(false);
  const [pwFailCount, setPwFailCount] = useState(0);
  // Admin email-code fallback: once the operator chooses "Email me a sign-in code", we send the code
  // to the SAME admin userid from the previous step and show a code-entry view IN PLACE (no going
  // back to re-type the userid, no "use a password instead" link).
  const [adminEmailOtp, setAdminEmailOtp] = useState(false);
  // User-tab SSO: the SSO teams discovered for the entered email after "Next". When non-empty the
  // credential pane shows one tile per team; `ssoMisconfigured` shows a server-config-error pane.
  const [ssoTeams, setSsoTeams] = useState<readonly SsoForEmail[]>([]);
  const [ssoMisconfigured, setSsoMisconfigured] = useState(false);
  // Issue 4: the user is SSO-only (no password) but their team's SSO was disabled — show the SSO
  // panel greyed out with a DISABLED overlay (unclickable), rather than a dead-end password prompt.
  const [ssoDisabled, setSsoDisabled] = useState(false);
  // User-tab governing team (per-team-signin-factors task 13.2 / R6.4): the `tenant_id` the server
  // resolved at the email step for a `totp` / `passkey` / `password` factor (the single active team,
  // or the user-chosen team for a multi-team coherent factor). Carried into the credential submit so
  // `handleLogin` reads the per-team factor and selects that team on success. `null` when the server
  // resolved none. The admin tab never sets this (its governing team is always the root team).
  const [governingTenantId, setGoverningTenantId] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");

  // Brand-panel platform totals (O / A / T / H). Loaded once; null until loaded or when unavailable.
  const [stats, setStats] = useState<PublicStats | null>(null);
  const { loadStats } = props;
  useEffect(() => {
    if (loadStats === undefined) return;
    let live = true;
    void loadStats().then(
      (s) => {
        if (live) setStats(s);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [loadStats]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<FriendlyAuthError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const errorId = useId();
  const noticeId = useId();
  const headingId = useId();

  const returnTo = useMemo<string>(() => {
    const state = location.state as { from?: { pathname?: string; search?: string } } | null;
    const from = state?.from?.pathname;
    if (typeof from !== "string" || from.length === 0) return "/";
    // C2: the enrollment approval page needs its `?user_code=` back after sign-in. Only that page keeps
    // its query string; every other return target stays path-only, as before.
    const search = state?.from?.search;
    return from === "/approve" && typeof search === "string" && search.startsWith("?") ? `${from}${search}` : from;
  }, [location.state]);

  // The SSO callback (per-team-signin-factors task 7) mints no session for a banned or already-
  // existing account and redirects back here carrying `?sso=…`. Classify the raw query value into a
  // typed outcome (task 12 `ssoOutcomeFromQuery`): `pending` (self-provisioned, awaiting approval),
  // `banned` (account-banned message, no session, no team), `account_exists` (an account already
  // exists for this email), or `none`.
  const ssoOutcome = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return ssoOutcomeFromQuery(sp.get("sso"));
  }, [location.search]);
  const ssoPending = ssoOutcome === "pending";

  // The account-banned terminal state (per-team-signin-factors task 13.4 / R13). Set true for
  // `/login?sso=banned` (the SSO callback outcome) OR when any credential path returns the
  // `account_banned` result (the server's `403 { error: account_banned }` after auth succeeds). When
  // true the account-banned message shows with identical copy on both tabs; no session is held and
  // no team is offered.
  const [accountBanned, setAccountBanned] = useState(false);
  // The combined account-banned terminal condition: either a credential path returned the typed
  // `account_banned` result, or the SSO callback redirected with `?sso=banned`. Identical copy on
  // both tabs; drives the terminal message and suppresses the credential forms below.
  const isAccountBanned = accountBanned || ssoOutcome === "banned";

  const resetFlow = useCallback(() => {
    setStep({ kind: "credentials" });
    setAdminStage("userid");
    setPasskeyFailed(false);
    setPwFailCount(0);
    setAdminEmailOtp(false);
    setSsoTeams([]);
    setSsoMisconfigured(false);
    setSsoDisabled(false);
    setGoverningTenantId(null);
    setAccountBanned(false);
    setCode("");
    setError(null);
    setNotice(null);
  }, []);

  // Admin usernames are the LOCAL PART only — the "@oathera.ai" suffix is fixed (shown as a visual
  // adornment and appended at submit). Strip anything from an "@" onward as the admin types or pastes
  // (e.g. a full "rohan@oathera.ai" becomes "rohan"), so the domain can never be doubled into
  // "rohan@oathera.ai@oathera.ai". Whitespace is removed too.
  const setAdminLocal = useCallback((value: string) => {
    const localPart = value.split("@", 1)[0] ?? "";
    setEmail(localPart.replace(/\s+/g, ""));
  }, []);

  const completeSession = useCallback(async () => {
    await auth.refresh();
    navigate(returnTo, { replace: true });
  }, [auth, navigate, returnTo]);

  // ---- Admin step 1: userid -> Next -----------------------------------------------------------
  // Ask the server which factor this admin has enabled, then branch: a passkey user is signed in
  // with their passkey immediately (no password); a TOTP user slides to the credential pane with the
  // password + OTP boxes shown together; everyone else slides to a password-only credential pane.
  const goToCredential = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(null);
      setNotice(null);
      const local = email.trim();
      if (local.length === 0) {
        setError(friendly("validation"));
        return;
      }
      // User tab (A6.1, A6.2): the operator types a FULL email, so validate its shape client-side
      // before any network request. A malformed email shows an inline error and makes NO call to the
      // server. The admin tab types only a username (the org domain is appended below), so the
      // full-email check does not apply there.
      if (!isAdmin && validateEmail(local, "Email", true) !== null) {
        setError(friendly("invalid_email"));
        return;
      }
      // Admins type only the username (the org domain is appended); users type a full email.
      const fullEmail = isAdmin ? adminEmail(local) : local;
      // A fresh "Next" discards any governing team resolved on a previous attempt (the admin tab
      // never sets one — its governing team is always the root team).
      setGoverningTenantId(null);
      setBusy(true);
      try {
        // The server reports the method the USER has ENABLED for their account (not what their team
        // merely offers): sso | passkey | totp | password. The user tab additionally resolves a
        // governing team (`tenant_id`) and may return the two terminal results `anomaly` / `no_team`
        // (per-team-signin-factors task 5.2/5.3). Branch on the factor.
        const result = await getLoginFactor(fullEmail, isAdmin ? "admin" : "user");

        if (result.factor === "sso" && result.ssoTeams.length > 0) {
          // The user enabled SSO — show a tile per team that has a provider; clicking one redirects
          // to that team's IdP (and signs the user into that team). For the user tab these tiles ARE
          // the governing-team options for the multi-team all-SSO case: picking a tile selects the
          // team (`startTeamSso` carries its `tenant_id` into the OIDC redirect), so no `tenantId`
          // is threaded through the password submit here.
          setSsoTeams(result.ssoTeams);
          setStep({ kind: "credentials" });
          setAdminStage("credential");
          return;
        }
        if (result.factor === "sso_misconfigured") {
          // SSO is enabled but no active team has a provider — a broken setup. Show a config error.
          setSsoMisconfigured(true);
          setStep({ kind: "credentials" });
          setAdminStage("credential");
          return;
        }

        if (result.factor === "sso_disabled") {
          // Issue 4: SSO-only account whose team disabled SSO. Show the credential pane with the
          // greyed-out, unclickable SSO panel + DISABLED overlay (handled in the render below).
          setSsoDisabled(true);
          setStep({ kind: "credentials" });
          setAdminStage("credential");
          return;
        }

        // User-tab terminal results (per-team-signin-factors task 5.2/5.3, task 13.3). Both are hard
        // stops: no credential pane, no login, and no network beyond the factor lookup. The operator
        // stays on the email pane with an explicit inline error so they can correct or go back. The
        // admin tab never receives these. `no_team` (R8.6/R8.7/R10.1) covers both no domain-SSO match
        // and a matched team without an SSO provider; `anomaly` (R7.6) is the incoherent-factor mix.
        if (result.factor === "no_team") {
          setError(friendly("no_team"));
          setStep({ kind: "credentials" });
          setAdminStage("userid");
          return;
        }
        if (result.factor === "anomaly") {
          setError(friendly("anomaly"));
          setStep({ kind: "credentials" });
          setAdminStage("userid");
          return;
        }

        if (result.factor === "passkey") {
          // The user's enabled factor is a passkey — start the ceremony immediately, no password.
          // Carry the governing team the server resolved so the session lands in that team.
          setGoverningTenantId(result.tenantId);
          setNotice("Use your passkey to sign in…");
          try {
            // The typed passkey helper (task 12) surfaces the server's `403 account_banned` as a
            // typed result rather than throwing, so a banned account proven by passkey shows the
            // account-banned message with no session held and no team offered (R13.1/R13.2).
            const outcome = await loginWithPasskeyResult();
            if (outcome.kind === "account_banned") {
              setNotice(null);
              setAccountBanned(true);
              return;
            }
            await completeSession();
            return;
          } catch (e) {
            // Passkey did not complete (cancelled or failed). It is the enabled factor, so we do NOT
            // fall back to a password; surface the email-code + reset affordances on the pane.
            setPasskeyFailed(true);
            if (e instanceof DOMException || (e instanceof Error && e.name === "NotAllowedError")) {
              setNotice("Passkey sign-in was cancelled.");
            } else {
              setError(friendlyAuthError(e));
            }
            setAdminStage("credential");
            return;
          }
        }

        // totp → password + OTP boxes together; password → password only. On the user tab both carry
        // the governing team the server resolved at the email step (the single active team, or the
        // user-chosen team for a multi-team coherent factor) into the credential submit so
        // `handleLogin` reads that team's factor and selects it on success (R6.4, R7.3–R7.5).
        setGoverningTenantId(result.tenantId);
        if (result.factor === "totp") {
          setStep({ kind: "mfa" });
          setNotice("Enter your password and the 6-digit code from your authenticator app.");
        } else {
          setStep({ kind: "credentials" });
        }
        setAdminStage("credential");
      } finally {
        setBusy(false);
      }
    },
    [email, isAdmin, completeSession],
  );

  const startTeamSso = useCallback((tenantId: string) => {
    const href = `/auth/login?idp=${encodeURIComponent(tenantId)}`;
    if (props.onSsoNavigate !== undefined) props.onSsoNavigate(href);
    else window.location.assign(href);
  }, [props]);

  // ---- Password + MFA ------------------------------------------------------------------------
  const submitPassword = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(null);
      setNotice(null);
      const local = email.trim();
      if (local.length === 0 || password.length === 0) {
        setError(friendly("validation"));
        return;
      }
      // Admins type only the username; the org domain is appended to form the full email.
      const fullEmail = isAdmin ? adminEmail(local) : local;
      setBusy(true);
      try {
        // The typed password helper (task 12) threads the governing team (per-team-signin-factors
        // task 13.2 / R6.4 — omitted on the admin tab, where it is always null, and when the server
        // resolved none, so the admin and single-team flows are byte-identical to before) and the
        // TOTP `code` when the step is `mfa`. It maps the server's `403 account_banned` to a typed
        // result and leaves every genuine credential failure (wrong password/OTP, lockout) throwing
        // an ApiError, so throttle/credential copy is unchanged.
        const result = await loginWithPassword({
          email: fullEmail,
          password,
          ...(step.kind === "mfa" && code.trim().length > 0 ? { code: code.trim() } : {}),
          ...(governingTenantId !== null ? { tenantId: governingTenantId } : {}),
        });
        if (result.kind === "mfa_required") {
          // Not a failure — the password was accepted and the server now wants the OTP. Do not count
          // this against the consecutive-failure total.
          setStep({ kind: "mfa" });
          setNotice("Enter the 6-digit code from your authenticator app.");
          return;
        }
        if (result.kind === "account_banned") {
          // Credentials were proven but the account/governing team is banned (R13.1/R13.2/R13.3):
          // show the account-banned message, hold no session, offer no team. Not a credential
          // failure, so the consecutive-failure count is left untouched.
          setAccountBanned(true);
          return;
        }
        setPwFailCount(0);
        await completeSession();
      } catch (e) {
        // A real credential failure (wrong password, or wrong OTP with the password). Count it; after
        // 3 consecutive failures the credential pane reveals the email-code + reset fallback.
        setPwFailCount((n) => n + 1);
        setError(friendlyAuthError(e));
      } finally {
        setBusy(false);
      }
    },
    [email, password, code, step.kind, isAdmin, governingTenantId, completeSession],
  );

  // ---- Email OTP (and register) --------------------------------------------------------------
  const requestOtp = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(null);
      setNotice(null);
      const local = email.trim();
      if (local.length === 0) {
        setError(friendly("validation"));
        return;
      }
      const fullEmail = isAdmin ? adminEmail(local) : local;
      setBusy(true);
      try {
        await authFetch<AuthEnvelope>("/auth/start", {
          method: "POST",
          body: { email: fullEmail },
        });
        setStep({ kind: "otp_sent" });
        setNotice("If that email is recognized, a sign-in code is on its way. Enter it below.");
      } catch (e) {
        setError(friendlyAuthError(e));
      } finally {
        setBusy(false);
      }
    },
    [email, isAdmin],
  );

  const verifyOtp = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(null);
      setNotice(null);
      if (code.trim().length === 0) {
        setError(friendly("validation"));
        return;
      }
      const fullEmail = isAdmin ? adminEmail(email) : email.trim();
      setBusy(true);
      try {
        // The typed email-code helper (task 12) maps the server's `403 account_banned` to a typed
        // result (R13.1 — the email sign-in code is one of the credential paths); every other
        // failure (wrong/expired code) throws an ApiError, so that copy is unchanged.
        const result = await verifyEmailCode(fullEmail, code.trim());
        if (result.kind === "account_banned") {
          setAccountBanned(true);
          return;
        }
        await completeSession();
      } catch (e) {
        setError(friendlyAuthError(e));
      } finally {
        setBusy(false);
      }
    },
    [email, code, isAdmin, completeSession],
  );

  // ---- Admin email-code fallback: send the code to the KNOWN userid, then show code entry --------
  const sendAdminEmailCode = useCallback(async () => {
    setError(null);
    setNotice(null);
    const fullEmail = isAdmin ? adminEmail(email) : email.trim();
    setBusy(true);
    try {
      await authFetch<AuthEnvelope>("/auth/start", { method: "POST", body: { email: fullEmail } });
      setCode("");
      setAdminEmailOtp(true);
      setPasskeyFailed(false);
      setPwFailCount(0);
      setNotice("A sign-in code is on its way to your email. Enter it below.");
    } catch (e) {
      setError(friendlyAuthError(e));
    } finally {
      setBusy(false);
    }
  }, [email, isAdmin]);

  // ---- Invite redemption (register with an invite code) --------------------------------------
  const submitInvite = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setError(null);
      setNotice(null);
      if (email.trim().length === 0 || code.trim().length === 0 || password.length === 0) {
        setError(friendly("validation"));
        return;
      }
      setBusy(true);
      try {
        // `/auth/invite/redeem` provisions the user, grants the invited team membership, sets the
        // password, and issues a session (sets the cookie). On success we are signed in.
        await authFetch<AuthEnvelope>("/auth/invite/redeem", {
          method: "POST",
          body: { token: code.trim(), email: email.trim(), password },
        });
        await completeSession();
      } catch (e) {
        setError(friendlyAuthError(e));
      } finally {
        setBusy(false);
      }
    },
    [email, code, password, completeSession],
  );

  // ---- OIDC SSO ------------------------------------------------------------------------------
  const startSso = useCallback(
    (idpId: string) => {
      setError(null);
      const href = `/auth/login?idp=${encodeURIComponent(idpId)}`;
      if (props.onSsoNavigate !== undefined) props.onSsoNavigate(href);
      else window.location.assign(href);
    },
    [props],
  );

  const idps = props.idps ?? [];

  const subheading =
    mode === "register"
      ? "Enter the email you were invited with and the invite code from your email, then create a password."
      : roleHint === "admin"
        ? "Sign in to manage your team, members, and roles."
        : "Sign in to the OATHERA control plane.";

  return (
    <div className="oa-auth oa-login">
      {/* LEFT — brand panel (navy/blue gradient). Decorative; hidden from AT and on narrow screens. */}
      <aside className="oa-login__brand" aria-hidden="true">
        {/* Descope-style floating testimonial cards drifting in/out at random positions. */}
        <FloatingQuotes />

        {/* Logo + tagline, vertically CENTERED in the panel. */}
        <div className="oa-login__brand-inner">
          <img src={controlplaneLogo} alt="" className="oa-login__brand-logo" />
          <p className="oa-login__brand-tagline">
            The control plane for your KYA flow: identity, enforcement, and integrity in one place.
          </p>
        </div>

        {/*
          Live-metrics pills, ANCHORED to the bottom of the panel: platform totals across every team
          from the public /public/stats route, each with its 7-day growth. When the total grew from 0
          in that window (no percentage exists) the badge reads "▲ new"; with no figure, no badge.
        */}
        <ul className="oa-login__metrics">
          {LIVE_METRICS.map((m) => {
            const s = stats?.[m.key];
            const value = s?.value ?? null;
            const delta = value === null ? null : (s?.delta7dPct ?? null);
            const isNew = value !== null && delta === null && s?.new7d === true;
            return (
              <li key={m.key} className="oa-metric-pill">
                <span className="oa-metric-pill__icon" data-key={m.key}>
                  {m.glyph}
                </span>
                <span className="oa-metric-pill__body">
                  <span className="oa-metric-pill__value">{formatStatValue(value)}</span>
                  <span className="oa-metric-pill__label">{m.label}</span>
                </span>
                {delta !== null && (
                  <span
                    className="oa-metric-pill__delta"
                    data-dir={delta >= 0 ? "up" : "down"}
                  >
                    {delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}%
                  </span>
                )}
                {isNew && (
                  <span className="oa-metric-pill__delta" data-dir="up">
                    ▲ new
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        {/* Copyright, anchored at the very bottom. OATHERA links to the marketing site. */}
        <p className="oa-login__copyright">
          {/* Inner span keeps the content in normal inline flow so the spaces around the OATHERA link
              are preserved; the <p> is a flex container only to vertically center it on the icon row. */}
          <span className="oa-login__copyright-text">
            © 2025{currentYear > 2025 ? `-${currentYear}` : ""}{" "}
            <a href="https://oathera.ai" className="oa-login__copyright-link" tabIndex={-1}>
              OATHERA
            </a>{" "}
            Security. All rights reserved.
          </span>
        </p>
      </aside>

      {/* RIGHT — form panel with an animated particle-network background. */}
      <main className="oa-login__panel" aria-labelledby={headingId}>
        <ParticleField />
        <div className="oa-login__card">
          <div className="oa-login__badge">
            <img src={oMark} alt="OATHERA" className="oa-login__mark" width={40} height={40} />
          </div>

          <h1 id={headingId} className="oa-login__heading">
            {isAdmin
              ? "Admin sign in"
              : mode === "register"
                ? "Accept your invite"
                : "Welcome back"}
          </h1>
          <p className="oa-login__subheading">{subheading}</p>

          {/* Role toggle (user / admin): copy-only affordance. */}
          <div className="oa-login__role" role="radiogroup" aria-label="Sign in as">
            <button
              type="button"
              role="radio"
              aria-checked={roleHint === "user"}
              className="oa-login__role-btn"
              data-active={roleHint === "user" ? "true" : undefined}
              onClick={() => {
                setRoleHint("user");
              }}
            >
              User
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={roleHint === "admin"}
              className="oa-login__role-btn"
              data-active={roleHint === "admin" ? "true" : undefined}
              onClick={() => {
                // Admins sign in only (no register) with username + password.
                setRoleHint("admin");
                setMode("login");
                setMechanism("password");
                resetFlow();
              }}
            >
              Admin
            </button>
          </div>

          {/* Mode tabs (login / register) — hidden for admins, who sign in only. */}
          {!isAdmin && (
          <div className="oa-login__modes" role="tablist" aria-label="Login or register">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "login"}
              className="oa-login__mode"
              data-active={mode === "login" ? "true" : undefined}
              onClick={() => {
                setMode("login");
                setMechanism("password");
                resetFlow();
              }}
            >
              Sign in
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "register"}
              className="oa-login__mode"
              data-active={mode === "register" ? "true" : undefined}
              onClick={() => {
                setMode("register");
                setMechanism("password");
                resetFlow();
              }}
            >
              Accept invite
            </button>
          </div>
          )}

          {ssoPending && (
            <p className="oa-login__notice" role="status" aria-live="polite">
              Your account was created and is awaiting administrator approval. Sign-in can&rsquo;t be
              completed until an administrator approves your request. Please contact your
              organization&rsquo;s administrator to expedite approval.
            </p>
          )}

          {/*
            Terminal messages (per-team-signin-factors task 13.4 / R13). The account-banned message
            shows AFTER authentication is refused — for `/login?sso=banned` (the SSO callback outcome)
            OR when any credential path returned the `account_banned` result — with IDENTICAL copy on
            both the user and admin tabs. It is a hard stop: no session is held and no team is offered,
            so the credential forms are suppressed below. The account-exists message shows for
            `/login?sso=account_exists`. Both are driven by the typed outcomes from account-client.
          */}
          {isAccountBanned ? (
            <p id={errorId} className="oa-login__error" role="alert">
              {AUTH_MESSAGES.account_banned}
            </p>
          ) : ssoOutcome === "account_exists" ? (
            <p id={errorId} className="oa-login__error" role="alert">
              {AUTH_MESSAGES.account_exists}
            </p>
          ) : null}

          {notice !== null && !isAccountBanned && (
            <p id={noticeId} className="oa-login__notice" role="status" aria-live="polite">
              {notice}
            </p>
          )}
          {error !== null && !isAccountBanned && (
            <p id={errorId} className="oa-login__error" role="alert">
              {error.message}
            </p>
          )}

          {isAccountBanned ? null : mode === "register" ? (
            <form
              className="oa-login__form"
              onSubmit={submitInvite}
              aria-describedby={error !== null ? errorId : undefined}
              noValidate
            >
              <Field
                label="Email"
                type="email"
                name="email"
                value={email}
                onChange={setEmail}
                autoComplete="username"
                disabled={busy}
                required
              />
              <Field
                label="Invite code"
                type="text"
                name="invite"
                value={code}
                onChange={setCode}
                autoComplete="off"
                disabled={busy}
                required
              />
              <Field
                label="Create a password"
                type="password"
                name="new-password"
                value={password}
                onChange={setPassword}
                autoComplete="new-password"
                disabled={busy}
                required
              />
              <button type="submit" className="oa-login__submit" disabled={busy}>
                {busy ? "Accepting…" : "Accept invite"}
              </button>
            </form>
          ) : mode === "login" && mechanism === "password" ? (
            // Sign-in is a two-step wizard with a slide: the email pane flies left as the credential
            // pane flies in from the right. The user tab adds an SSO tile; the admin tab uses the
            // same machinery with a username+domain field.
            <div className="oa-login__wizard" data-stage={adminStage}>
              <div className="oa-login__panes" data-stage={adminStage}>
                {/* Pane 1 — userid + Next */}
                <form
                  className="oa-login__pane oa-login__form"
                  onSubmit={goToCredential}
                  aria-hidden={adminStage !== "userid"}
                  aria-describedby={error !== null ? errorId : undefined}
                  noValidate
                >
                  {isAdmin ? (
                    <Field
                      label="Username"
                      type="text"
                      name="username"
                      value={email}
                      onChange={setAdminLocal}
                      autoComplete="username"
                      suffix={ADMIN_EMAIL_DOMAIN}
                      disabled={adminStage !== "userid" || busy}
                      required
                      autoFocus
                    />
                  ) : (
                    <Field
                      label="Email"
                      type="email"
                      name="email"
                      value={email}
                      onChange={setEmail}
                      autoComplete="username"
                      disabled={adminStage !== "userid" || busy}
                      required
                      autoFocus
                    />
                  )}
                  <button type="submit" className="oa-login__submit" disabled={busy || email.trim().length === 0}>
                    {busy ? "Checking…" : "Next"}
                  </button>
                </form>

                {/* Pane 2 — password (+ OTP on mfa_required) + Login, OR the email-code entry/fallback */}
                <form
                  className="oa-login__pane oa-login__form"
                  onSubmit={adminEmailOtp ? verifyOtp : submitPassword}
                  aria-hidden={adminStage !== "credential"}
                  aria-describedby={error !== null ? errorId : undefined}
                  noValidate
                >
                  <div className="oa-login__who">
                    <span className="oa-login__who-label">Signing in as</span>
                    <span className="oa-login__who-value">{isAdmin ? adminEmail(email) : email.trim()}</span>
                    <button
                      type="button"
                      className="oa-login__link"
                      onClick={() => {
                        setAdminStage("userid");
                        setStep({ kind: "credentials" });
                        setAdminEmailOtp(false);
                        setPasskeyFailed(false);
                        setPwFailCount(0);
                        setSsoTeams([]);
                        setSsoMisconfigured(false);
                        setSsoDisabled(false);
                        setGoverningTenantId(null);
                        setPassword("");
                        setCode("");
                        setError(null);
                        setNotice(null);
                      }}
                    >
                      Change
                    </button>
                  </div>
                  {ssoDisabled ? (
                    // Issue 4: SSO-only account whose team disabled SSO. The SSO panel is shown
                    // greyed out and UNCLICKABLE, with a translucent DISABLED overlay spread across
                    // its width; hovering shows a red border. The user cannot sign in this way until
                    // an admin re-enables SSO (or gives them a password).
                    <>
                      <div className="oa-sso-login-panel oa-login__sso-tile" data-disabled="true" aria-disabled="true">
                        <span className="oa-login__sso-tile-body">
                          <span className="oa-login__sso-tile-org">Single sign-on</span>
                          <span className="oa-login__sso-tile-msg">
                            Single sign-on is turned off for your team.
                          </span>
                        </span>
                        <span className="oa-sso-disabled-overlay" aria-hidden="true">DISABLED</span>
                      </div>
                      <p className="oa-login__notice" role="status" aria-live="polite">
                        Single sign-on has been disabled for your team, and your account has no
                        password. Ask your team administrator to re-enable single sign-on or to set
                        up another sign-in method for you.
                      </p>
                      <button type="button" className="oa-login__submit oa-login__submit--ghost" onClick={() => window.location.reload()}>
                        Start over
                      </button>
                    </>
                  ) : ssoMisconfigured ? (
                    // SSO is enabled but no team has a provider configured — a broken setup.
                    <>
                      <p className="oa-login__error" role="alert">
                        Single sign-on is enabled for your account, but no team is configured with a
                        provider. This is a server configuration issue — please contact your
                        administrator.
                      </p>
                      <button type="button" className="oa-login__submit oa-login__submit--ghost" onClick={() => window.location.reload()}>
                        Start over
                      </button>
                    </>
                  ) : ssoTeams.length > 0 ? (
                    // One tile per team that has SSO; clicking one starts that team's OIDC redirect
                    // (and signs the user into that team). No password/OTP for an SSO user.
                    <>
                      {ssoTeams.length > 1 && (
                        <p className="oa-login__notice" role="status">
                          You belong to more than one team with single sign-on. Choose the one to sign in to.
                        </p>
                      )}
                      {ssoTeams.map((t) => (
                        <button
                          key={t.tenantId}
                          type="button"
                          className="oa-login__sso-tile"
                          disabled={busy}
                          onClick={() => startTeamSso(t.tenantId)}
                        >
                          {t.providerLogoUrl !== null && (
                            <img src={t.providerLogoUrl} alt="" width={36} height={36} />
                          )}
                          <span className="oa-login__sso-tile-body">
                            <span className="oa-login__sso-tile-org">{t.orgName}</span>
                            <span className="oa-login__sso-tile-msg">
                              Sign in with single sign-on via {t.providerLabel}.
                            </span>
                          </span>
                        </button>
                      ))}
                    </>
                  ) : adminEmailOtp ? (
                    // Email-code entry: the code was already sent to this userid. Enter it and
                    // verify — no re-typing the email, no "use a password instead" link.
                    <>
                      <div className="oa-login__field">
                        <span className="oa-login__label">Sign-in code</span>
                        <OtpInput value={code} onChange={setCode} disabled={busy} length={6} autoFocus />
                      </div>
                      <button
                        type="submit"
                        className="oa-login__submit"
                        disabled={busy || code.replace(/\D/g, "").length < 6}
                      >
                        {busy ? "Verifying…" : "Verify code"}
                      </button>
                      <div className="oa-login__fallback">
                        <button
                          type="button"
                          className="oa-login__submit oa-login__submit--ghost"
                          disabled={busy}
                          onClick={() => void sendAdminEmailCode()}
                        >
                          Resend code
                        </button>
                        <button
                          type="button"
                          className="oa-login__submit oa-login__submit--ghost"
                          onClick={() => window.location.reload()}
                        >
                          Reset
                        </button>
                      </div>
                    </>
                  ) : passkeyFailed || pwFailCount >= 3 ? (
                    // Fallback mode: the passkey did not complete, OR 3 consecutive password/OTP
                    // attempts failed. Hide the password/OTP fields and the submit button entirely;
                    // offer only the email-code fallback and a reset to start over.
                    <AdminFallback busy={busy} onEmailCode={() => void sendAdminEmailCode()} />
                  ) : (
                    <>
                      <Field
                        label="Password"
                        type="password"
                        name="password"
                        value={password}
                        onChange={setPassword}
                        autoComplete="current-password"
                        disabled={busy}
                        required
                        autoFocus={adminStage === "credential"}
                      />
                      {step.kind === "mfa" && (
                        <div className="oa-login__field">
                          <span className="oa-login__label">Authentication code</span>
                          <OtpInput value={code} onChange={setCode} disabled={busy} length={6} autoFocus />
                        </div>
                      )}
                      <button
                        type="submit"
                        className="oa-login__submit"
                        disabled={busy || (step.kind === "mfa" && code.replace(/\D/g, "").length < 6)}
                      >
                        {busy ? "Signing in…" : step.kind === "mfa" ? "Verify code" : "Log in"}
                      </button>
                    </>
                  )}
                </form>
              </div>
            </div>
          ) : (
            <form
              className="oa-login__form"
              onSubmit={step.kind === "otp_sent" ? verifyOtp : requestOtp}
              aria-describedby={error !== null ? errorId : undefined}
              noValidate
            >
              {isAdmin ? (
                <Field
                  label="Username"
                  type="text"
                  name="username"
                  value={email}
                  onChange={setAdminLocal}
                  autoComplete="username"
                  suffix={ADMIN_EMAIL_DOMAIN}
                  disabled={step.kind === "otp_sent" || busy}
                  required
                />
              ) : (
                <Field
                  label="Email"
                  type="email"
                  name="email"
                  value={email}
                  onChange={setEmail}
                  autoComplete="username"
                  disabled={step.kind === "otp_sent" || busy}
                  required
                />
              )}
              {step.kind === "otp_sent" && (
                <div className="oa-login__field">
                  <span className="oa-login__label">Sign-in code</span>
                  <OtpInput value={code} onChange={setCode} disabled={busy} length={6} autoFocus required />
                </div>
              )}
              <button type="submit" className="oa-login__submit" disabled={busy}>
                {busy ? "Working…" : step.kind === "otp_sent" ? "Verify code" : "Send sign-in code"}
              </button>
              {mode === "login" && (
                <div className="oa-login__alt">
                  <button
                    type="button"
                    className="oa-login__link"
                    onClick={() => {
                      setMechanism("password");
                      resetFlow();
                    }}
                  >
                    Use a password instead
                  </button>
                </div>
              )}
            </form>
          )}


          {idps.length > 0 && (
            <div className="oa-login__sso">
              <div className="oa-login__divider" aria-hidden="true">
                <span>or</span>
              </div>
              <ul className="oa-login__sso-list">
                {idps.map((idp) => (
                  <li key={idp.id}>
                    <button
                      type="button"
                      className="oa-login__sso-btn"
                      disabled={busy}
                      onClick={() => {
                        startSso(idp.id);
                      }}
                    >
                      {idp.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Social links, anchored to the bottom-right of the form panel. Open in a new tab. */}
        <nav className="oa-login__social" aria-label="OATHERA social links">
          <a
            className="oa-login__social-link"
            href="https://linkedin.com/company/oathera"
            target="_blank"
            rel="noreferrer noopener"
            aria-label="OATHERA on LinkedIn (opens in a new tab)"
            title="LinkedIn"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true" focusable="false">
              <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.8 0 0 .78 0 1.73v20.53C0 23.22.8 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .78 23.2 0 22.22 0z" />
            </svg>
          </a>
          <a
            className="oa-login__social-link"
            href="https://github.com/OATHERA/"
            target="_blank"
            rel="noreferrer noopener"
            aria-label="OATHERA on GitHub (opens in a new tab)"
            title="GitHub"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true" focusable="false">
              <path d="M12 .5C5.73.5.5 5.73.5 12c0 5.08 3.29 9.39 7.86 10.91.57.1.78-.25.78-.55 0-.27-.01-1.17-.02-2.12-3.2.7-3.88-1.37-3.88-1.37-.52-1.33-1.28-1.69-1.28-1.69-1.05-.72.08-.7.08-.7 1.16.08 1.77 1.19 1.77 1.19 1.03 1.76 2.7 1.25 3.36.96.1-.75.4-1.26.73-1.55-2.55-.29-5.24-1.28-5.24-5.68 0-1.26.45-2.28 1.19-3.09-.12-.29-.52-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11.1 11.1 0 0 1 2.9-.39c.98 0 1.97.13 2.9.39 2.2-1.49 3.17-1.18 3.17-1.18.63 1.59.23 2.76.11 3.05.74.81 1.19 1.83 1.19 3.09 0 4.41-2.69 5.38-5.25 5.67.41.36.78 1.07.78 2.16 0 1.56-.01 2.82-.01 3.2 0 .31.21.67.79.55A11.51 11.51 0 0 0 23.5 12C23.5 5.73 18.27.5 12 .5z" />
            </svg>
          </a>
          <a
            className="oa-login__social-link"
            href="https://x.com/OATHERAai"
            target="_blank"
            rel="noreferrer noopener"
            aria-label="OATHERA on X (opens in a new tab)"
            title="X"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true" focusable="false">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.656l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
            </svg>
          </a>
        </nav>
      </main>
    </div>
  );
}

/**
 * The admin sign-in fallback controls, shown when the passkey login did not complete or after three
 * consecutive failed password / password+OTP attempts: a button to switch to the email sign-in-code
 * flow, and a Reset button that reloads the page so the operator starts the whole flow over.
 */
function AdminFallback(props: { readonly busy: boolean; readonly onEmailCode: () => void }): ReactNode {
  return (
    <div className="oa-login__fallback">
      <button
        type="button"
        className="oa-login__submit"
        disabled={props.busy}
        onClick={props.onEmailCode}
      >
        Email me a sign-in code instead
      </button>
      <button
        type="button"
        className="oa-login__submit oa-login__submit--ghost"
        onClick={() => window.location.reload()}
      >
        Reset
      </button>
    </div>
  );
}

/** A labelled text input with the label programmatically tied to the control (a11y). */
interface FieldProps {
  readonly label: string;
  readonly type: "email" | "password" | "text";
  readonly name: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly autoComplete?: string;
  readonly inputMode?: "numeric" | "text" | "email";
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly autoFocus?: boolean;
  /** A fixed, non-editable trailing adornment shown inside the field (e.g. "@oathera.ai"). */
  readonly suffix?: string;
}

function Field(props: FieldProps): ReactNode {
  const id = useId();
  const input = (
    <input
      id={id}
      className="oa-login__input"
      type={props.type}
      name={props.name}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      {...(props.autoComplete === undefined ? {} : { autoComplete: props.autoComplete })}
      {...(props.inputMode === undefined ? {} : { inputMode: props.inputMode })}
      {...(props.disabled === true ? { disabled: true } : {})}
      {...(props.required === true ? { required: true } : {})}
      {...(props.autoFocus === true ? { autoFocus: true } : {})}
    />
  );
  return (
    <div className="oa-login__field">
      <label className="oa-login__label" htmlFor={id}>
        {props.label}
      </label>
      {props.suffix === undefined ? (
        input
      ) : (
        <div className="oa-login__input-group" data-disabled={props.disabled === true ? "true" : undefined}>
          {input}
          <span className="oa-login__input-suffix" aria-hidden="true">
            @{props.suffix.replace(/^@/, "")}
          </span>
        </div>
      )}
    </div>
  );
}
